import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import { useContext } from '@nuxtjs/composition-api';
import { FormsProvider } from '@webplatform/asbis-ui/dist/forms';
import { kebabToPascal, prepareDynamicBlock } from '~/helpers';
import ProductDetails from '~/components/sections/productSection/ProductDetails.vue';
import ProductSeriesHeader from '~/components/sections/productSection/ProductSeriesHeader.vue';
import ProductSeries from '~/components/sections/productSection/ProductSeries.vue';
import ProductWarrantyBanner from '~/components/sections/productSection/ProductWarrantyBanner.vue';
import ProductContentBanner from '~/components/sections/productSection/ProductContentBanner.vue';
import ProductFeatures from '~/components/sections/productSection/ProductFeatures.vue';
import ProductFeaturesBanner from '~/components/sections/productSection/ProductFeaturesBanner.vue';
import ProductCertificates from '~/components/sections/productSection/ProductCertificates.vue';
import LatestNews from '~/components/sections/newsSection/LatestNews.vue';
import BannersSlider from '~/components/sections/sliderSection/BannersSlider.vue';
import SolutionsCarousel from '~/components/sections/sliderSection/SolutionsCarousel.vue';
import CategoriesSection from '~/components/sections/categorySection/CategoriesSection.vue';
import CategoryBanner from '~/components/sections/categorySection/CategoryBanner.vue';
import CategoryBannerV2 from '~/components/sections/categorySection/CategoryBannerV2.vue';
import CategoryInfoBlocks from '~/components/sections/categorySection/CategoryInfoBlocks.vue';
import CategoryFeatures from '~/components/sections/categorySection/CategoryFeatures.vue';
import CategoryAccessories from '~/components/sections/categorySection/CategoryAccessories.vue';
import CategoryApplications from '~/components/sections/categorySection/CategoryApplications.vue';
import CategoryFaq from '~/components/sections/categorySection/CategoryFaq.vue';
import CategoryProductSeries from '~/components/sections/categorySection/CategoryProductSeries.vue';
import CategoryRecommended from '~/components/sections/categorySection/CategoryRecommended.vue';
import CategoryRecommendedProducts from '~/components/sections/categorySection/CategoryRecommendedProducts.vue';
import SoftwareCategoryFirstScreen from '~/components/sections/categorySection/SoftwareCategoryFirstScreen.vue';
import SoftwareCategoryAwards from '~/components/sections/categorySection/SoftwareCategoryAwards.vue';
import SoftwareCategoryInfoblocks from '~/components/sections/categorySection/SoftwareCategoryInfoblocks.vue';
import SoftwareCategoryReviewsSlider from '~/components/sections/categorySection/SoftwareCategoryReviewsSlider.vue';
import SoftwareCategoryTryDemo from '~/components/sections/categorySection/SoftwareCategoryTryDemo.vue';
import SoftwareCategoryTryDemoFormModal from '~/components/sections/categorySection/SoftwareCategoryTryDemoFormModal.vue';
import SoftwareCategoryChoosePlan from '~/components/sections/categorySection/SoftwareCategoryChoosePlan.vue';
import SoftwareCategoryUserHelp from '~/components/sections/categorySection/SoftwareCategoryUserHelp.vue';
import CatalogCategories from '~/components/sections/categorySection/CatalogCategories.vue';
import ConsultationForm from '~/components/sections/formSection/ConsultationForm.vue';
import ServiceCentersSection from '~/components/sections/serviceCentersSection/ServiceCentersSection.vue';
import CtaSectionWithBackground from '~/components/sections/ctaSection/CtaSectionWithBackground.vue';
import CtaSectionBannerLocal from '~/components/sections/ctaSection/CtaSectionBannerLocal.vue';
import ProductBenefits from '~/components/sections/productSection/ProductBenefits.vue';
import ProductFootnote from '~/components/sections/productSection/ProductFootnote.vue';
import ProductVideo from '~/components/sections/productSection/ProductVideo.vue';
import PartnersSection from '~/components/sections/categorySection/PartnersSection.vue';
import ServiceForm from '~/components/sections/formSection/ServiceForm.vue';
import CatalogHead from '~/domains/catalog/components/CatalogHead.vue';
import HtmlContentBlock from '~/components/HtmlContentBlock.vue';
import BookMeetingPopup from '~/domains/book-meeting/components/BookMeetingPopup.vue';
import { PreparedBlock, RawBlock } from '~/domains/constructor-pages/types';
import { AbSectionWidget } from '@webplatform/asbis-ui/dist/integrations/nuxt2';
import type { FormMetaInfo } from '@webplatform/asbis-ui/dist/forms';
import { sections } from '@webplatform/asbis-ui/dist/sections';

interface Props {
  blocks?: any[];
  product?: any;
  metaInfo?: FormMetaInfo;
}

type EnhancedBlock = PreparedBlock & { isAsbisUi: boolean };


export default /*#__PURE__*/_defineComponent({
  __name: 'PageBlocks',
  props: {
    blocks: { default: () => [] },
    product: { default: () => null },
    metaInfo: { default: () => ({}) }
  },
  setup(__props: any) {

const props = __props as { blocks: any[], product: any, metaInfo: FormMetaInfo };

const SECTIONS_WITH_DATA_AND_PRODUCT_PROPS = ['ProductSeriesHeader'];

const FORM_SECTIONS = ['form-section-simple', 'form-section-text-image'];
const EXCLUDED_BLOCKS = [
  'product-series-additional-information',
  'software-documents',
  'marketing_type',
];

// Mapping for sections that have both local (json) and asbis-ui versions
const LOCAL_SECTION_TYPE_MAPPING: Record<string, string> = {
  'cta-section-banner': 'cta-section-banner-local',
};

const LOCAL_SECTIONS_WRAP_DATA_PROPS = [
  'CatalogCategories',
  'CatalogHead',
  'ProductSeriesHeader',
  'ProductFeaturesBanner',
  'ProductCertificates',
  'ProductWarrantyBanner',
  'ProductContentBanner',
  'ProductFeatures',
  'BannersSlider',
  'CategoriesSection',
  'SolutionsCarousel',
  'LatestNews',
  'CategoryBanner',
  'CategoryBannerV2',
  'CategoryInfoBlocks',
  'CategoryFeatures',
  'CategoryProductSeries',
  'CategoryApplications',
  'CategoryAccessories',
  'CategoryRecommended',
  'CategoryFaq',
  'SoftwareCategoryFirstScreen',
  'SoftwareCategoryAwards',
  'SoftwareCategoryInfoblocks',
  'SoftwareCategoryReviewsSlider',
  'SoftwareCategoryTryDemo',
  'SoftwareCategoryTryDemoFormModal',
  'SoftwareCategoryChoosePlan',
  'SoftwareCategoryUserHelp',
  'BookMeetingPopup',
  'CategoryRecommendedProducts',
  'ServiceCentersSection',
  'CtaSectionWithBackground',
  'CtaSectionBannerLocal',
  'ProductBenefits',
  'ProductFootnote',
  'ProductVideo',
  'PartnersSection',
  'HtmlContentBlock',
];

const LOCAL_SECTIONS: Record<string, any> = {
  ProductDetails,
  ProductSeriesHeader,
  ProductSeries,
  ProductWarrantyBanner,
  ProductContentBanner,
  ProductFeatures,
  ProductFeaturesBanner,
  ProductCertificates,
  LatestNews,
  BannersSlider,
  SolutionsCarousel,
  CategoriesSection,
  CategoryBanner,
  CategoryBannerV2,
  CategoryInfoBlocks,
  CategoryFeatures,
  CategoryAccessories,
  CategoryApplications,
  CategoryFaq,
  CategoryProductSeries,
  CategoryRecommended,
  CategoryRecommendedProducts,
  SoftwareCategoryFirstScreen,
  SoftwareCategoryAwards,
  SoftwareCategoryInfoblocks,
  SoftwareCategoryReviewsSlider,
  SoftwareCategoryTryDemo,
  SoftwareCategoryTryDemoFormModal,
  SoftwareCategoryChoosePlan,
  SoftwareCategoryUserHelp,
  CatalogCategories,
  ConsultationForm,
  ServiceCentersSection,
  CtaSectionWithBackground,
  CtaSectionBannerLocal,
  ProductBenefits,
  ProductFootnote,
  ProductVideo,
  PartnersSection,
  ServiceForm,
  CatalogHead,
  HtmlContentBlock,
  BookMeetingPopup,
};



const { $gtm, $config, i18n } = useContext();

const formsConfig = {
  baseUrl: $config.baseUrl,
  locale: i18n.locale,
};

const locale = computed<string>(() => i18n?.locale || 'en');

const isAsbisUiRawBlock = (block: any): boolean => {
  const sectionType = block?.content?.type ?? block?.type;
  return (
    (block?.type === 'asbis-ui' || block?.content_type === 'asbis-ui') &&
    kebabToPascal(sectionType) in sections
  );
};

const preparedComponents = computed<EnhancedBlock[]>(() =>
  (props.blocks || [])
    .map((block) => {
      const isAsbisUi = isAsbisUiRawBlock(block);

      const blockToProcess = { ...block };

      if (!isAsbisUi && block?.content_type === 'json') {
        // catalog pages
        blockToProcess.type =
          LOCAL_SECTION_TYPE_MAPPING[blockToProcess?.type] ||
          blockToProcess?.type;
      } else if (!isAsbisUi && block.type === 'json' && block.content.type) {
        // static pages
        blockToProcess.content.type =
          LOCAL_SECTION_TYPE_MAPPING[blockToProcess?.content.type] ||
          blockToProcess?.content.type;
      }

      const prepared = prepareDynamicBlock(blockToProcess as RawBlock, {
        excludedBlocks: EXCLUDED_BLOCKS,
        sectionsWithDataProps: LOCAL_SECTIONS_WRAP_DATA_PROPS,
      });

      return {
        isAsbisUi,
        ...prepared,
      };
    })
    .filter((c): c is EnhancedBlock => !!c?.type),
);

const getComponentType = (componentType: string) =>
  kebabToPascal(componentType);

const getBindingData = (component: PreparedBlock) => {
  if (!component || !component.data) return {};
  if (
    SECTIONS_WITH_DATA_AND_PRODUCT_PROPS.includes(kebabToPascal(component.type))
  ) {
    return { data: component.data.data, product: props.product };
  }
  return component.data;
};

const shouldHandleFormEvents = (componentType: string) => {
  return FORM_SECTIONS.includes(componentType);
};

const onFormStart = () => {
  $gtm.push({ event: 'form_start' });
};

const onSubmitSuccess = () => {
  $gtm.push({ event: 'submit_form' });
};

const getEventHandlers = (componentType: string) => {
  const handlers: Record<string, () => void> = {};

  if (shouldHandleFormEvents(componentType)) {
    handlers['form-start'] = onFormStart;
    handlers['submit-success'] = onSubmitSuccess;
  }

  return handlers;
};

const getLocalSection = (componentType: string) =>
  // unknown types still resolve by name through the global registration
  LOCAL_SECTIONS[getComponentType(componentType)] ?? componentType;

const renderedBlocks = computed(() =>
  preparedComponents.value.map((component, i) =>
    component.isAsbisUi
      ? {
          key: `ab-${i}`,
          type: component.type,
          component: AbSectionWidget,
          props: {
            sectionName: component.type,
            data: { ...component.data, isFirstComponent: i === 0 },
            locale: locale.value,
            metaInfo: props.metaInfo,
          },
        }
      : {
          key: `local-${i}`,
          type: component.type,
          component: getLocalSection(component.type),
          props: {
            ...getBindingData(component),
            'data-test': getComponentType(component.type),
          },
        },
  ),
);

return { __sfc: true,SECTIONS_WITH_DATA_AND_PRODUCT_PROPS, FORM_SECTIONS, EXCLUDED_BLOCKS, LOCAL_SECTION_TYPE_MAPPING, LOCAL_SECTIONS_WRAP_DATA_PROPS, LOCAL_SECTIONS, props, $gtm, $config, i18n, formsConfig, locale, isAsbisUiRawBlock, preparedComponents, getComponentType, getBindingData, shouldHandleFormEvents, onFormStart, onSubmitSuccess, getEventHandlers, getLocalSection, renderedBlocks, FormsProvider }
}

})